import { useCallback, useEffect, useRef, useState } from "react";
import JobApi from "services/JobApi";
import { BatchJob } from "reducers/types";
import { useAppDispatch } from "store";
import { setError } from "reducers/message";
import { changed, nextDelay, PollingIntervals } from "hooks/jobPolling";

// Reusable job tracking for one job type, addressed by its base path (e.g. "/admin/resources/update/tefas/history").
// The server decides which job to show (GET {base}/current); this hook only polls it (setTimeout chain, never
// overlapping), backs off on errors, stops on unmount and wraps the actions. Knows nothing about TEFAS or the cache.

export type JobConnection = "ok" | "retrying" | "auth";

export interface UseJobPollingOptions extends Partial<PollingIntervals> {
  basePath: string;
}

export interface UseJobPolling {
  job: BatchJob | null;
  queued: BatchJob[];
  loading: boolean;
  connection: JobConnection;
  start: (params: object) => Promise<BatchJob | undefined>;
  cancel: (jobId?: string) => Promise<boolean>;
  approve: (jobId?: string) => Promise<boolean>;
  retry: (itemKeys?: string[], jobId?: string) => Promise<boolean>;
  refresh: () => void;
}

const DEFAULTS: PollingIntervals = {
  activeIntervalMs: 2000,
  waitingIntervalMs: 30000,
  queuedIntervalMs: 10000,
  hiddenIntervalMs: 15000,
  maxBackoffMs: 30000,
};

const useJobPolling = (options: UseJobPollingOptions): UseJobPolling => {
  const { basePath } = options;
  const intervalsRef = useRef<PollingIntervals>({ ...DEFAULTS, ...options });
  intervalsRef.current = { ...DEFAULTS, ...options };

  const dispatch = useAppDispatch();
  const [job, setJob] = useState<BatchJob | null>(null);
  const [queued, setQueued] = useState<BatchJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<JobConnection>("ok");

  const mounted = useRef(true);
  const stopped = useRef(false);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobRef = useRef<BatchJob | null>(null);
  const queuedRef = useRef<BatchJob[]>([]);
  const errorCount = useRef(0);

  const showJob = useCallback((next: BatchJob | null) => {
    jobRef.current = next;
    setJob(next);
  }, []);

  const showQueued = useCallback((next: BatchJob[]) => {
    queuedRef.current = next;
    setQueued(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const tick = useCallback(async () => {
    if (!mounted.current || stopped.current) {
      return;
    }
    clearTimer();
    const mySeq = ++seq.current;
    const current = () => mounted.current && mySeq === seq.current;

    try {
      // light poll first (no item payload); fetch items only when the shown job changed
      const light = await JobApi.current(basePath, false);
      if (!current()) return;
      showQueued(light.queued);
      if (changed(jobRef.current, light.job)) {
        const full = light.job ? await JobApi.current(basePath, true) : light;
        if (!current()) return;
        showQueued(full.queued);
        showJob(full.job);
      }
      errorCount.current = 0;
      setConnection("ok");
    } catch (e) {
      if (!current()) return;
      if (e && (e as Error).message === "AUTH_REQUIRED") {
        stopped.current = true;
        setConnection("auth");
        dispatch(setError("AUTH_REQUIRED"));
        return;
      }
      errorCount.current += 1;
      setConnection("retrying");
    }

    if (!current() || stopped.current) return;
    setLoading(false);
    const delay = nextDelay(jobRef.current, queuedRef.current.length, errorCount.current, document.hidden, intervalsRef.current);
    if (delay !== null) {
      timer.current = setTimeout(tick, delay);
    }
  }, [basePath, dispatch, showJob, showQueued, clearTimer]);

  useEffect(() => {
    mounted.current = true;
    stopped.current = false;
    showJob(null);
    showQueued([]);
    setLoading(true);
    tick();
    const onVisibility = () => {
      if (!document.hidden) {
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      mounted.current = false;
      seq.current += 1;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [basePath, tick, showJob, showQueued, clearTimer]);

  const act = async (action: () => Promise<BatchJob>): Promise<BatchJob | undefined> => {
    try {
      return await action();
    } catch (e) {
      if (e && (e as Error).message === "AUTH_REQUIRED") {
        dispatch(setError("AUTH_REQUIRED"));
      }
      return undefined;
    } finally {
      tick(); // show the real server state, whatever happened
    }
  };

  // Actions name the job the card shows (echoed from the server's view), so a click can't hit another job.
  const targetId = (jobId?: string) => jobId || (jobRef.current ? jobRef.current.jobId : undefined);

  const start = (params: object) => act(() => JobApi.start(basePath, params));

  const cancel = async (jobId?: string) => {
    const id = targetId(jobId);
    return id ? (await act(() => JobApi.cancel(basePath, id))) !== undefined : false;
  };

  const approve = async (jobId?: string) => {
    const id = targetId(jobId);
    return id ? (await act(() => JobApi.approve(basePath, id))) !== undefined : false;
  };

  const retry = async (itemKeys?: string[], jobId?: string) => {
    const id = targetId(jobId);
    return id ? (await act(() => JobApi.retry(basePath, id, itemKeys))) !== undefined : false;
  };

  return {
    job,
    queued,
    loading,
    connection,
    start,
    cancel,
    approve,
    retry,
    refresh: tick,
  };
};

export default useJobPolling;
